import { motion } from 'framer-motion';
import { FaApple, FaGooglePlay, FaGlobe } from 'react-icons/fa';
import { Globe, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import loginBg from '../assets/login-bg.png';
import logoWhite from '../assets/logo-versarc-white.png';

const ComingSoon = () => {
    const { language, toggleLanguage } = useLanguage();

    return (
        <div 
            className="min-h-screen w-full relative flex flex-col justify-between text-versarc-200 overflow-x-hidden selection:bg-versarc-600/30 bg-versarc-950 font-sans"
            style={{ 
                backgroundImage: `url(${loginBg})`, 
                backgroundSize: 'cover', 
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="absolute inset-0 bg-versarc-950/80 backdrop-blur-[2px] z-0" />

            {/* Top Navigation Bar with Language Switcher */}
            <header className="w-full max-w-7xl mx-auto px-6 pt-8 pb-4 relative z-10 flex justify-end">
                <button
                    onClick={toggleLanguage}
                    className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-versarc-900/80 border border-versarc-800 text-xs text-versarc-200 hover:border-versarc-600 transition-colors backdrop-blur-md"
                >
                    <Globe size={14} className="text-versarc-400" />
                    <span className="font-medium font-sans">{language === 'en' ? '中文' : 'English'}</span>
                </button>
            </header>

            {/* Centered Minimal & Elegant Content */}
            <main className="relative z-10 w-full max-w-3xl mx-auto px-6 py-8 flex flex-col items-center text-center my-auto">
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-col items-center"
                >
                    {/* Brand Logo with Pure Transparent Background */}
                    <img 
                        src={logoWhite} 
                        alt="Versarc" 
                        className="h-20 sm:h-24 md:h-28 w-auto object-contain drop-shadow-lg mb-4" 
                    />

                    {/* Brand Name */}
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-[0.28em] uppercase text-versarc-200">
                        VERSARC
                    </h1>

                    {/* Coming Soon Headline */}
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-light italic text-versarc-400 mt-2 mb-6 tracking-tight font-serif">
                        {language === 'zh' ? '敬请期待' : 'Coming Soon'}
                    </h2>

                    {/* China to Ghana Route with Arrow */}
                    <div className="flex items-center justify-center gap-3 sm:gap-4 text-base sm:text-lg md:text-xl font-light tracking-[0.25em] uppercase text-versarc-200 mb-12">
                        <span>{language === 'zh' ? '中国' : 'China'}</span>
                        <ArrowRight size={20} className="text-versarc-400 stroke-[1.5]" />
                        <span>{language === 'zh' ? '加纳' : 'Ghana'}</span>
                    </div>

                    {/* Platform Availability Section */}
                    <div className="w-full flex flex-col items-center">
                        <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-versarc-400 mb-4">
                            {language === 'zh' ? '支持平台' : 'Available On'}
                        </p>

                        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                            {/* Web */}
                            <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-versarc-900 border border-versarc-800 text-versarc-200 shadow-md min-w-[150px] justify-center hover:bg-versarc-800 transition-colors cursor-default">
                                <FaGlobe className="w-5 h-5 text-versarc-200" />
                                <div className="text-left">
                                    <div className="text-[9px] uppercase tracking-wider text-versarc-400 font-medium leading-none">
                                        {language === 'zh' ? '网页端' : 'Access via'}
                                    </div>
                                    <div className="text-xs font-bold tracking-wide text-versarc-200 leading-tight mt-0.5">
                                        Web
                                    </div>
                                </div>
                            </div>

                            {/* Apple App Store */}
                            <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-versarc-900 border border-versarc-800 text-versarc-200 shadow-md min-w-[150px] justify-center hover:bg-versarc-800 transition-colors cursor-default">
                                <FaApple className="w-5 h-5 text-versarc-200" />
                                <div className="text-left">
                                    <div className="text-[9px] uppercase tracking-wider text-versarc-400 font-medium leading-none">
                                        {language === 'zh' ? '前往下载' : 'Download on'}
                                    </div>
                                    <div className="text-xs font-bold tracking-wide text-versarc-200 leading-tight mt-0.5">
                                        App Store
                                    </div>
                                </div>
                            </div>

                            {/* Google Play */}
                            <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-versarc-900 border border-versarc-800 text-versarc-200 shadow-md min-w-[150px] justify-center hover:bg-versarc-800 transition-colors cursor-default">
                                <FaGooglePlay className="w-4 h-4 text-versarc-200" />
                                <div className="text-left">
                                    <div className="text-[9px] uppercase tracking-wider text-versarc-400 font-medium leading-none">
                                        {language === 'zh' ? '前往下载' : 'Get it on'}
                                    </div>
                                    <div className="text-xs font-bold tracking-wide text-versarc-200 leading-tight mt-0.5">
                                        Google Play
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </main>

            {/* Clean Footer */}
            <footer className="relative z-10 w-full max-w-7xl mx-auto px-6 py-6 flex items-center justify-center text-[10px] tracking-widest uppercase text-versarc-600 font-medium">
                © 2026 VERSARC. ALL RIGHTS RESERVED.
            </footer>
        </div>
    );
};

export default ComingSoon;
